import type { User } from '@/api/types'
import { BBDTag } from '@gravitate-js/excalibrr'
import type { ICellRendererParams } from 'ag-grid-community'
import { Switch } from 'antd'
import React from 'react'
import { Role } from '../types'
import { formatRoleName } from '../utils'

export const EmailVerifiedRenderer: React.FC<{ value: boolean }> = ({
  value,
}) => (
  <BBDTag
    color={value ? 'success' : 'default'}
    style={{ width: 'min-content' }}
  >
    {value ? 'Verified' : 'Unverified'}
  </BBDTag>
)

export function DomainsRenderer({ data, context }: ICellRendererParams<User>) {
  const domains = data?.domains || []
  const roles = context.roles as Role[]

  if (domains.length === 0) {
    return <span className='text-gray-400'>No domains</span>
  }

  return (
    <div
      className='flex items-center flex-wrap'
      style={{ gap: 4, padding: '2px 0' }}
    >
      {domains.map((domain, index: number) => {
        const roleLabels = (domain.roles || [])
          .map((roleId) => roles.find((x) => x._id === roleId)?.name || roleId)
          .map(formatRoleName)
          .filter((label): label is string => label.length > 0)
        const uniqueRoleLabels = Array.from(new Set(roleLabels))

        const isGravitate = domain.tenant_name === 'Gravitate'
        const roleText =
          uniqueRoleLabels.length > 0
            ? ` (${uniqueRoleLabels.join(', ')})`
            : isGravitate
              ? ' (All)'
              : ''

        return (
          <BBDTag
            key={`${domain.tenant_name}-${index}`}
            {...(isGravitate ? { theme2: true } : {})}
          >
            {domain.tenant_name}
            {roleText}
          </BBDTag>
        )
      })}
    </div>
  )
}

export type ActiveSwitchRendererProps = {
  data: User
  onToggle: (user: User) => void
}

export function ActiveSwitchRenderer({
  data,
  onToggle,
}: ActiveSwitchRendererProps) {
  return (
    <div className='flex items-center justify-center w-full'>
      <Switch
        size='small'
        checked={data.is_active}
        onChange={() => onToggle(data)}
      />
    </div>
  )
}
